import React, { Fragment, useCallback, useState, useMemo, ChangeEventHandler } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import ExperimentItem from './ExperimentItem'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EmptyObject, objectEntries } from 'lib/object/objectUtils'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Divider from 'components/Luxkit/Divider'
import { sortBy } from 'lib/array/arrayUtils'

type ExperimentEntry = [string, App.OptimizelyExperimentDetails]

function ExperimentControlPanel() {
  const optimizelyExperiments = useAppSelector(state => state.optimizely.optimizelyExperiments ?? EmptyObject)
  const [searchValue, setSearchValue] = useState('')
  const displayList = useMemo(() => {
    return sortBy(
      objectEntries(optimizelyExperiments),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is ExperimentEntry => {
      const [, exp] = entry
      return exp !== undefined
    })
  }, [optimizelyExperiments])

  const filteredExperiments = useMemo(() => {
    if (!searchValue) return displayList
    return displayList.filter(([experimentId]) =>
      experimentId.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }, [displayList, searchValue])

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
  }, [])

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSearchValue(e.target.value)
  }, [])

  const hasExperiments = Object.keys(optimizelyExperiments).length > 0

  return (
    <ModalBase mode="drawer-starboard">
      <ModalHeader title="AB Control Panel">
        <SearchFormTextField
          label="Search experiments"
          placeholder="search by key"
          value={searchValue}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
        />
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          {!hasExperiments && <BodyText variant="large">No experiments found.</BodyText>}

          {hasExperiments && (
            <VerticalSpacer gap={12}>
              {filteredExperiments.length === 0 && <BodyText variant="large">No matching experiments</BodyText>}
              {filteredExperiments.map(([experimentId, experiment], index) => (
                <Fragment key={experimentId}>
                  {index > 0 && <Divider kind="secondary" />}
                  <ExperimentItem
                    experimentId={experimentId}
                    experiment={experiment}
                  />
                </Fragment>
              ))}
            </VerticalSpacer>
          )}
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default ExperimentControlPanel
